// 在庫管理。出荷ロット単位の在庫サマリ + 材料単位の集計。
// 現在庫 = 搬入済QR数 − 消費済QR数。

import { Box, Stack, Typography, Chip, Paper, Table, TableHead, TableBody, TableRow, TableCell, Alert, LinearProgress, Link as MuiLink, Tooltip } from "@mui/material";
import InventoryIcon from "@mui/icons-material/Inventory";
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { PageContext } from "../PageContext";
import { useDbSlice } from "../../../api/dbSlice";
import { materialSearchUrl } from "../../../util/Materialperser";
import { DataLink, MasterLink } from "../../../components/links/EntityLinks";
import { WORD } from "../../../word";
import { renderWord } from "../../../util/renderWord";
import type { ExtendedDB } from "../../../types/extendedDB";
import { FULL_EPS, formatRatioAsBagsKg, getMaterialSetKg } from "../../../util/consumption";
import type { ConstructionMaterialConsumptionBase } from "../../../schema/generated/ConstructionMaterialConsumption";
import { ContentLoading } from "../../../components/Loading";

type LotSummary = {
  lot_id: string;
  carry_out_id: string;
  material_id?: number;
  material_name: string;
  lot_no: string;
  total: number;
  carried_in: number;
  consumed: number;
  not_shipped: number;
  in_stock: number;
  partial_count: number;
  in_stock_ids: number[];
  partial_ids: number[];
  not_shipped_ids: number[];
  set_kg: number;
};

type MaterialSummary = {
  material_id: number;
  name: string;
  total: number;
  carried_in: number;
  consumed: number;
  in_stock: number;
  partial_count: number;
  set_kg: number;
  lots: LotSummary[];
};

function isActive(row: { deleted_at?: number | null } | undefined): boolean {
  return row == null || row.deleted_at == null;
}

// QRごとの累計消費割合(0..1 clamp)。alive な ConstructionMaterialConsumption 行(deleted_at null)の Σ used_ratio。
function buildRatioByQrId(cmc: ConstructionMaterialConsumptionBase[]): Map<number, number> {
  const totals = new Map<number, number>();
  for (const row of cmc) {
    if (row.deleted_at != null) {
      continue;
    }
    totals.set(row.material_qr_id, (totals.get(row.material_qr_id) ?? 0) + row.used_ratio);
  }
  const clamped = new Map<number, number>();
  for (const [qrId, total] of totals) {
    clamped.set(qrId, Math.min(1, Math.max(0, total)));
  }
  return clamped;
}

export function buildStockSummaries(db: Pick<ExtendedDB, "lots" | "materialQrs" | "materials" | "constructionMaterialConsumptions" | "materialDetails">): {
  lotsSummary: LotSummary[];
  materialSummary: MaterialSummary[];
} {
  const activeMaterials = new Map(db.materials.filter(isActive).map((m) => [m.id, m]));
  const ratioByQrId = buildRatioByQrId(db.constructionMaterialConsumptions);

  const lotsSummary = db.lots.filter(isActive).map((lot) => {
    const qrs = db.materialQrs.filter((q) => q.lot_id === lot.id);
    const carriedIn = qrs.filter((q) => q.carry_in_id);
    const notShipped = qrs.filter((q) => !q.carry_in_id);

    let consumedRatioSum = 0;
    let inStockRatioSum = 0;
    const inStockIds: number[] = [];
    const partialIds: number[] = [];
    for (const q of carriedIn) {
      const ratio = ratioByQrId.get(q.id) ?? 0;
      const remaining = 1 - ratio;
      consumedRatioSum += ratio;
      inStockRatioSum += remaining;
      if (remaining > FULL_EPS) {
        inStockIds.push(q.id);
      }
      if (ratio > 0 && ratio < 1 - FULL_EPS) {
        partialIds.push(q.id);
      }
    }

    const mat = lot.material_id != null ? activeMaterials.get(lot.material_id) : undefined;
    return {
      lot_id: lot.id,
      carry_out_id: lot.carry_out_id,
      material_id: lot.material_id ?? undefined,
      material_name: mat?.name ?? "(材料未指定)",
      lot_no: lot.lot_no ?? "(LotNo未記録)",
      total: qrs.length,
      carried_in: carriedIn.length,
      consumed: consumedRatioSum,
      not_shipped: notShipped.length,
      in_stock: inStockRatioSum,
      partial_count: partialIds.length,
      in_stock_ids: inStockIds,
      partial_ids: partialIds,
      not_shipped_ids: notShipped.map((q) => q.id),
      set_kg: lot.material_id != null ? getMaterialSetKg(db.materialDetails, lot.material_id) : 0,
    };
  });

  const map = new Map<number, MaterialSummary>();
  for (const l of lotsSummary) {
    if (l.material_id === undefined || !activeMaterials.has(l.material_id)) continue;
    const emptyLots: LotSummary[] = [];
    const cur = map.get(l.material_id) ?? {
      material_id: l.material_id,
      name: l.material_name,
      total: 0,
      carried_in: 0,
      consumed: 0,
      in_stock: 0,
      partial_count: 0,
      set_kg: getMaterialSetKg(db.materialDetails, l.material_id),
      lots: emptyLots,
    };
    cur.total += l.total;
    cur.carried_in += l.carried_in;
    cur.consumed += l.consumed;
    cur.in_stock += l.in_stock;
    cur.partial_count += l.partial_count;
    cur.lots.push(l);
    map.set(l.material_id, cur);
  }
  return { lotsSummary, materialSummary: Array.from(map.values()) };
}

function calcConsumptionRate(carriedIn: number, consumed: number): number {
  if (carriedIn) {
    return (consumed / carriedIn) * 100;
  }
  return 0;
}

function renderStockStatusChip(inStock: number, consumptionRate: number) {
  if (inStock <= 0) {
    return <Chip size="small" color="error" label="在庫切れ" />;
  }
  if (consumptionRate >= 70) {
    return <Chip size="small" color="warning" label="残少" />;
  }
  return <Chip size="small" color="success" label="OK" />;
}

export default function StockPage({ pageContext }: { pageContext: PageContext }) {
  const { db, loading } = useDbSlice(pageContext.api.indexedDB, pageContext.api.currentUser.id, ["lots", "materialQrs", "materials", "constructionMaterialConsumptions", "materialDetails"]);
  const navigate = useNavigate();

  const { lotsSummary, materialSummary } = useMemo(() => {
    return buildStockSummaries(db);
  }, [db.lots, db.materialQrs, db.materials, db.constructionMaterialConsumptions, db.materialDetails]);

  const gotoSearchInStock = (ids: number[]) => {
    if (!ids.length) return;
    navigate(materialSearchUrl(ids));
  };

  if (loading) {
    return <ContentLoading />;
  }

  return (
    <Stack p={3} maxWidth={1300} mx="auto" gap={3}>
      <Stack direction="row" alignItems="center" gap={1}>
        <InventoryIcon color="action" />
        <Typography variant="h4" fontWeight="bold">在庫管理</Typography>
        <Chip size="small" color="info" label={`${lotsSummary.length} Lot / ${materialSummary.length} 材料`} />
      </Stack>
      <Alert severity="info">
        {renderWord(WORD.stock.infoMain)}
        <Typography variant="caption" color="text.secondary">
          {renderWord(WORD.stock.infoNote)}
        </Typography>
      </Alert>

      <Paper variant="outlined">
        <Box p={3}>
          <Typography variant="h6" gutterBottom>材料別 在庫サマリ</Typography>
          <Typography variant="caption" color="text.secondary" display="block" mb={1}>
            「現在庫」=搬入済QRの残量合計(袋・kg)。QRごとの Σ 使用量を差し引いた値です。クリックすると材料検索ページで個体一覧を確認できます。
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: "bold" }}>材料</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>貼付済</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>搬入済</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>消費済</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>現在庫(残量)</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>残量あり</TableCell>
                <TableCell sx={{ fontWeight: "bold", width: 200 }}>消費率</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>状況</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {materialSummary.length === 0 && (
                <TableRow><TableCell colSpan={8} align="center" sx={{ py: 3, color: "text.secondary" }}>データがありません</TableCell></TableRow>
              )}
              {materialSummary.map((m) => {
                const allInStockIds = m.lots.flatMap((l) => l.in_stock_ids);
                const allPartialIds = m.lots.flatMap((l) => l.partial_ids);
                const inStock = allInStockIds.length;
                const consumptionRate = calcConsumptionRate(m.carried_in, m.consumed);
                return (
                  <TableRow key={m.material_id} hover>
                    <TableCell>
                      <Stack>
                        <MasterLink type="material" id={m.material_id} name={
                          <Typography variant="body2" fontWeight="bold">{m.name}</Typography>
                        } />
                        <Typography variant="caption" color="text.secondary">{m.lots.length} Lot</Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>{m.total}</TableCell>
                    <TableCell>{m.carried_in}</TableCell>
                    <TableCell>{formatRatioAsBagsKg(m.consumed, m.set_kg)}</TableCell>
                    <TableCell>
                      <Tooltip title={inStock > 0 ? "クリックで在庫の個体を材料検索で表示" : ""}>
                        <Box>
                          <MuiLink
                            component="button"
                            disabled={!inStock}
                            onClick={() => gotoSearchInStock(allInStockIds)}
                            underline="hover"
                            sx={{ fontWeight: "bold", fontSize: 18, color: inStock > 0 ? "primary.main" : "text.disabled" }}
                          >
                            {formatRatioAsBagsKg(m.in_stock, m.set_kg)}
                          </MuiLink>
                        </Box>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <MuiLink
                        component="button"
                        disabled={!m.partial_count}
                        onClick={() => gotoSearchInStock(allPartialIds)}
                        underline="hover"
                        sx={{ color: m.partial_count > 0 ? "warning.main" : "text.disabled" }}
                      >
                        {m.partial_count}個
                      </MuiLink>
                    </TableCell>
                    <TableCell>
                      <LinearProgress variant="determinate" value={Math.min(consumptionRate, 100)} sx={{ height: 8, borderRadius: 4 }} />
                      <Typography variant="caption">{consumptionRate.toFixed(0)}% 消費</Typography>
                    </TableCell>
                    <TableCell>
                      {renderStockStatusChip(inStock, consumptionRate)}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>
      </Paper>

      <Paper variant="outlined">
        <Box p={3}>
          <Typography variant="h6" gutterBottom>Lot別 内訳</Typography>
          <Typography variant="caption" color="text.secondary" display="block" mb={1}>
            出荷ロット単位の進捗。未搬入の数量から、追加発注の判断材料に。
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: "bold" }}>材料ロット</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>材料 / LotNo</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>貼付済</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>未搬入</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>搬入済</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>消費済</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>在庫</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {lotsSummary.length === 0 && (
                <TableRow><TableCell colSpan={7} align="center" sx={{ py: 3, color: "text.secondary" }}>材料ロットがありません</TableCell></TableRow>
              )}
              {lotsSummary.map((lot) => (
                <TableRow key={lot.lot_id} hover>
                  <TableCell><DataLink type="lot" id={lot.lot_id} parentId={lot.carry_out_id} /></TableCell>
                  <TableCell>
                    <MasterLink type="material" id={lot.material_id ?? null} name={lot.material_name} /> / {lot.lot_no}
                  </TableCell>
                  <TableCell>{lot.total}</TableCell>
                  <TableCell>
                    <MuiLink
                      component="button"
                      disabled={!lot.not_shipped}
                      onClick={() => gotoSearchInStock(lot.not_shipped_ids)}
                      underline="hover"
                      sx={{ color: lot.not_shipped > 0 ? "warning.main" : "text.disabled" }}
                    >
                      {lot.not_shipped}
                    </MuiLink>
                  </TableCell>
                  <TableCell>{lot.carried_in}</TableCell>
                  <TableCell>{formatRatioAsBagsKg(lot.consumed, lot.set_kg)}</TableCell>
                  <TableCell>
                    <MuiLink
                      component="button"
                      disabled={!lot.in_stock_ids.length}
                      onClick={() => gotoSearchInStock(lot.in_stock_ids)}
                      underline="hover"
                      sx={{ fontWeight: "bold", color: lot.in_stock_ids.length ? "primary.main" : "text.disabled" }}
                    >
                      {formatRatioAsBagsKg(lot.in_stock, lot.set_kg)}
                    </MuiLink>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      </Paper>
    </Stack>
  );
}
